/**
 * @file themeParser.js
 * @description Parses markdown theme files with YAML front matter into CSS custom properties.
 * 
 * Exports:
 *   - parseTheme(markdownString) → flat object of CSS custom property key-value pairs
 *   - applyTheme(cssVarsObject) → applies CSS custom properties to document.documentElement
 *   - getThemeMeta(markdownString) → { id, name, description } from YAML front matter
 */

import { load as yamlLoad } from 'js-yaml';

/**
 * Extracts YAML front matter from a markdown string.
 * Expects the block between the first and second `---` delimiters.
 * 
 * @param {string} markdownString - The raw markdown file content.
 * @returns {string} The YAML string between the two `---` delimiters.
 */
function extractFrontMatter(markdownString) {
  const content = markdownString.trim();
  if (!content.startsWith('---')) {
    return '';
  }
  const secondDelimiter = content.indexOf('---', 3);
  if (secondDelimiter === -1) {
    return '';
  }
  return content.substring(3, secondDelimiter).trim();
}

/**
 * Resolves token references of the form `{section.key}` or `{section.key.subkey}` 
 * recursively against the parsed YAML data object.
 * 
 * @param {string} value - The string value potentially containing token references.
 * @param {Object} data - The full parsed YAML data object.
 * @param {number} depth - Current recursion depth to prevent infinite loops.
 * @returns {string} The fully resolved value string.
 */
function resolveTokenReferences(value, data, depth = 0) {
  if (typeof value !== 'string') {
    return String(value);
  }
  if (depth > 10) {
    console.warn(`[themeParser] Max resolution depth reached for value: "${value}"`);
    return value;
  }

  // Match all {section.key} or {section.key.subkey} patterns
  const tokenRegex = /\{([^}]+)\}/g;
  let resolved = value;
  let match;

  while ((match = tokenRegex.exec(value)) !== null) {
    const tokenPath = match[1]; // e.g., "colors.primary" or "typography.body-sm.fontFamily"
    const parts = tokenPath.split('.');
    
    let current = data;
    let found = true;
    for (const part of parts) {
      if (current && typeof current === 'object' && part in current) {
        current = current[part];
      } else {
        found = false;
        break;
      }
    }

    if (found && current !== undefined) {
      const resolvedValue = resolveTokenReferences(String(current), data, depth + 1);
      resolved = resolved.replace(match[0], resolvedValue);
    } else {
      console.warn(`[themeParser] Unresolvable token reference: "${match[0]}" — using fallback "inherit"`);
      resolved = resolved.replace(match[0], 'inherit');
    }
  }

  return resolved;
}

/**
 * Parses a markdown theme string and produces a flat object of CSS custom properties.
 * 
 * Naming conventions:
 *   - Colors → --theme-color-{key}
 *   - Typography → --theme-type-{token}-{property}
 *   - Spacing → --theme-spacing-{key}
 *   - Rounded → --theme-rounded-{key}
 *   - Components → --theme-{component-name}-{property}
 * 
 * @param {string} markdownString - The raw markdown file content with YAML front matter.
 * @returns {Object} A flat object where keys are CSS custom property names and values are resolved strings.
 */
export function parseTheme(markdownString) {
  const yamlStr = extractFrontMatter(markdownString);
  if (!yamlStr) {
    console.warn('[themeParser] No valid YAML front matter found.');
    return {};
  }

  let data;
  try {
    data = yamlLoad(yamlStr);
  } catch (e) {
    console.warn('[themeParser] Failed to parse YAML:', e.message);
    return {};
  }

  if (!data || typeof data !== 'object') {
    return {};
  }

  const cssVars = {};

  // Colors
  if (data.colors && typeof data.colors === 'object') {
    for (const [key, value] of Object.entries(data.colors)) {
      cssVars[`--theme-color-${key}`] = resolveTokenReferences(value, data);
    }
  }

  // Typography
  if (data.typography && typeof data.typography === 'object') {
    for (const [tokenName, props] of Object.entries(data.typography)) {
      if (props && typeof props === 'object') {
        for (const [prop, value] of Object.entries(props)) {
          // Convert camelCase property names to kebab-case for CSS
          const cssProp = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
          cssVars[`--theme-type-${tokenName}-${cssProp}`] = resolveTokenReferences(value, data);
        }
      }
    }
  }

  // Spacing
  if (data.spacing && typeof data.spacing === 'object') {
    for (const [key, value] of Object.entries(data.spacing)) {
      cssVars[`--theme-spacing-${key}`] = resolveTokenReferences(value, data);
    }
  }

  // Rounded
  if (data.rounded && typeof data.rounded === 'object') {
    for (const [key, value] of Object.entries(data.rounded)) {
      cssVars[`--theme-rounded-${key}`] = resolveTokenReferences(value, data);
    }
  }

  // Components
  if (data.components && typeof data.components === 'object') {
    for (const [componentName, props] of Object.entries(data.components)) {
      if (props && typeof props === 'object') {
        for (const [prop, value] of Object.entries(props)) {
          cssVars[`--theme-${componentName}-${prop}`] = resolveTokenReferences(value, data);
        }
      }
    }
  }

  return cssVars;
}

/**
 * Applies a flat CSS custom properties object to the document root element.
 * 
 * @param {Object} cssVarsObject - Object with CSS custom property name/value pairs.
 */
export function applyTheme(cssVarsObject) {
  if (!cssVarsObject || typeof cssVarsObject !== 'object') {
    return;
  }

  // Clear previously applied theme variables to prevent bleed-through
  const rootStyle = document.documentElement.style;
  const keysToRemove = [];
  for (let i = 0; i < rootStyle.length; i++) {
    const propName = rootStyle.item(i);
    if (propName && propName.startsWith('--theme-')) {
      keysToRemove.push(propName);
    }
  }
  keysToRemove.forEach((key) => rootStyle.removeProperty(key));

  for (const [key, value] of Object.entries(cssVarsObject)) {
    document.documentElement.style.setProperty(key, value);
  }
}

/**
 * Extracts theme metadata from markdown YAML front matter.
 * 
 * @param {string} markdownString - The raw markdown file content.
 * @returns {{ id: string, name: string, description: string }} Theme metadata.
 */
export function getThemeMeta(markdownString) {
  const yamlStr = extractFrontMatter(markdownString);
  if (!yamlStr) {
    return { id: '', name: 'Unknown', description: '' };
  }

  let data;
  try {
    data = yamlLoad(yamlStr);
  } catch (e) {
    return { id: '', name: 'Unknown', description: '' };
  }

  return {
    id: (data.name || 'Unknown').toLowerCase().replace(/\s+/g, '-'),
    name: data.name || 'Unknown',
    description: data.description || ''
  };
}
